import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { timeAgo, formatMeetingType, MEETING_TYPE_OPTIONS } from "@/lib/utils-ros";
import {
  AlertTriangle, CalendarClock, Check, ChevronDown, ChevronRight,
  Loader2, Minus, Plus, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import { ActionEditor } from "@/components/revenueos/ActionEditor";

const SENTIMENT_COLOR = {
  positive: "var(--success)", neutral: "var(--text-muted)",
  negative: "var(--danger)", mixed: "var(--warning)"
};

const MEETING_FILTERS = [
  { key: "all", label: "All meetings", icon: CalendarClock },
  { key: "positive", label: "Positive", icon: Check },
  { key: "neutral", label: "Neutral", icon: Minus },
  { key: "attention", label: "Needs attention", icon: AlertTriangle },
];

const asText = (a) => {
  if (typeof a === "string") return a;
  if (a && typeof a === "object") return a.action || a.title || a.task || String(a);
  return String(a ?? "");
};

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [analyzeOpen, setAnalyzeOpen] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/meetings");
      setMeetings(data);
    } catch {
      toast.error("Could not load meetings");
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const filterCounts = useMemo(() => ({
    all: meetings.length,
    positive: meetings.filter(m => m.ai_sentiment === "positive").length,
    neutral: meetings.filter(m => m.ai_sentiment === "neutral").length,
    attention: meetings.filter(m => ["negative", "mixed"].includes(m.ai_sentiment)).length,
  }), [meetings]);

  const visibleMeetings = useMemo(() => {
    if (filter === "all") return meetings;
    if (filter === "attention") return meetings.filter(m => ["negative", "mixed"].includes(m.ai_sentiment));
    return meetings.filter(m => m.ai_sentiment === filter);
  }, [filter, meetings]);

  async function analyze() {
    if (!transcript.trim()) return;
    setAnalyzing(true);
    try {
      await api.post("/meetings/analyze", { meeting_id: analyzeOpen.id, transcript });
      toast.success("Meeting analyzed · action items extracted");
      setAnalyzeOpen(null);
      setTranscript("");
      await load();
    } catch { toast.error("Analysis failed"); }
    finally { setAnalyzing(false); }
  }

  return (
    <div className="min-h-full bg-[#F7FAFC] pb-12" data-testid="meetings-page">
      <section className="bg-[#247DA8] px-5 md:px-9 py-5 md:py-6 flex items-center justify-between gap-4 shadow-[inset_0_-1px_0_rgba(255,255,255,0.18)]">
        <div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#FFFFFF]">Meetings</h1>
        </div>
        <button
          onClick={() => setLogOpen(true)}
          data-testid="add-meeting"
          aria-label="Log meeting"
          title="Log meeting"
          className="w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-[#FFFFFF] text-[#FFFFFF] flex items-center justify-center hover:bg-[#FFFFFF] hover:text-[#07539A] hover:shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition-all shrink-0"
        >
          <Plus size={26} />
        </button>
      </section>

      <nav className="relative z-10 mx-3 md:mx-8 -mt-3 md:-mt-4 bg-white rounded-full border border-[#DCE8EE] shadow-[0_9px_24px_rgba(7,83,154,0.12)] p-1.5 flex items-center gap-1 overflow-x-auto" aria-label="Meeting filters">
        {MEETING_FILTERS.map(item => {
          const active = filter === item.key;
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setFilter(item.key)}
              data-testid={`meeting-filter-${item.key}`}
              className={`min-w-max flex-1 h-10 px-3 md:px-5 rounded-full flex items-center justify-center gap-2 text-[12px] md:text-[13px] font-medium outline-none focus-visible:ring-2 focus-visible:ring-[#00A2D4]/40 transition-all ${active ? "bg-[var(--copper)] text-[#FFFFFF] shadow-[0_5px_12px_rgba(214,138,46,0.26)]" : "text-[#243B53] hover:bg-[#EDF7FB] hover:text-[#07539A]"}`}
            >
              <Icon size={13} />
              <span>{item.label}</span>
              <span className={`font-mono text-[10px] tabular-nums ${active ? "text-[#FFFFFF]" : "text-[#78909C]"}`}>{filterCounts[item.key]}</span>
            </button>
          );
        })}
      </nav>

      <div className="px-4 md:px-8 pt-7 md:pt-8">
        <div className="grid gap-4">
          {loading ? (
            <div className="min-h-44 rounded-[22px] border border-[#DCE8EE] bg-white flex items-center justify-center text-[#607D8B] shadow-[0_8px_24px_rgba(7,83,154,0.06)]">
              <Loader2 size={18} className="animate-spin mr-2 text-[#00A2D4]" /> Loading meetings…
            </div>
          ) : visibleMeetings.length === 0 ? (
            <div className="min-h-52 rounded-[22px] border border-dashed border-[#BFD5E0] bg-white/80 flex flex-col items-center justify-center text-center px-6 shadow-[0_8px_24px_rgba(7,83,154,0.04)]">
              <span className="w-11 h-11 rounded-full bg-[#E7F5FB] text-[#07539A] flex items-center justify-center mb-3"><CalendarClock size={20} /></span>
              <div className="font-display text-lg text-[#102A43]">No meetings here</div>
              <p className="text-[12px] text-[#78909C] mt-1">Choose another filter or log a new customer conversation.</p>
            </div>
          ) : visibleMeetings.map(m => {
            const isExp = expanded === m.id;
            return (
              <article key={m.id} className="group relative overflow-hidden rounded-[22px] border border-[#C9DCE6] bg-white shadow-[0_9px_25px_rgba(7,83,154,0.08)] hover:border-[#91C6DB] hover:shadow-[0_14px_34px_rgba(7,83,154,0.14)] transition-all" data-testid={`meeting-${m.id}`}>
                <div className="absolute left-0 top-5 bottom-5 w-2 rounded-r-full bg-[#1687EA]" />
                <button
                  onClick={() => setExpanded(isExp ? null : m.id)}
                  data-testid={`meeting-expand-${m.id}`}
                  className="w-full text-left p-5 pl-7 md:p-6 md:pl-9"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap text-[10px]">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#FFF5E8] text-[#B26E1F] font-semibold uppercase tracking-wider">{formatMeetingType(m.meeting_type)}</span>
                        <span className="text-[#78909C]">{timeAgo(m.scheduled_at)}</span>
                        <span className="w-1 h-1 rounded-full bg-[#B0BEC5]" />
                        <span className="text-[#78909C]">{m.duration_min} min</span>
                      </div>
                      <div className="font-display text-lg text-[#101820]">{m.title}</div>
                      {m.opportunity?.name && <div className="text-[12px] font-medium text-[#07539A] mt-1">{m.opportunity.name}</div>}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span className="text-[10px] uppercase tracking-wider px-3 py-1 rounded-full font-medium" style={{
                        background: `${SENTIMENT_COLOR[m.ai_sentiment]}15`, color: SENTIMENT_COLOR[m.ai_sentiment], border: `1px solid ${SENTIMENT_COLOR[m.ai_sentiment]}30`
                      }}>{m.ai_sentiment}</span>
                      <span className="w-7 h-7 rounded-full bg-[#EDF7FB] text-[#07539A] flex items-center justify-center group-hover:bg-[#E1F1F7] transition-colors">
                        {isExp ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                      </span>
                    </div>
                  </div>

                  {m.ai_summary && <div className="mt-4 rounded-xl bg-[#F8FCFE] border border-[#E2EEF3] px-4 py-3 text-[13px] text-[#526D7A] leading-relaxed">{m.ai_summary}</div>}

                </button>

                {isExp && (
                  <div className="border-t border-[#D9E7ED] p-5 md:p-6 bg-[#F8FCFE] space-y-3" data-testid={`meeting-editor-${m.id}`}>
                    <ActionEditor
                      actions={(m.ai_action_items || []).map(asText)}
                      title="Meeting action items"
                      showInsight={false}
                      accent="navy"
                      rounded
                      onSave={async (arr) => {
                        const { data } = await api.patch(`/meetings/${m.id}`, { ai_action_items: arr });
                        await load();
                        return data;
                      }}
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={(e) => { e.stopPropagation(); setAnalyzeOpen(m); setTranscript(m.transcript || ""); }}
                        data-testid={`re-transcribe-${m.id}`}
                        className="h-8 px-3 rounded-lg bg-[#E5F3F9] border border-[#BDD8E4] text-[#07539A] text-[10px] font-medium shadow-[0_3px_8px_rgba(7,83,154,0.08)] hover:bg-[#07539A] hover:text-[#FFFFFF] transition-all flex items-center gap-1.5"
                      >
                        <Sparkles size={11} /> Paste new transcript
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>

      {analyzeOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setAnalyzeOpen(null)}>
          <div className="bg-[#EFF8FC] border border-[#BFD9E5] shadow-[0_28px_70px_rgba(7,83,154,0.22)] rounded-[26px] w-full max-w-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="analyze-modal">
            <div className="p-5 border-b border-[#D5E7EF] flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-white text-[#07539A] flex items-center justify-center"><Sparkles size={15} /></span>
              <div className="flex-1 font-display text-lg text-[#07539A]">Analyze: {analyzeOpen.title}</div>
              <button onClick={() => setAnalyzeOpen(null)} data-testid="close-analyze" className="w-8 h-8 rounded-full hover:bg-white text-[#607D8B] flex items-center justify-center"><X size={15} /></button>
            </div>
            <div className="p-5 md:p-6 space-y-4">
              <div className="text-[12px] text-[#607D8B]">Paste or edit the transcript. AI will extract the summary, sentiment and action items, then create the relevant tasks.</div>
              <textarea
                value={transcript} onChange={(e) => setTranscript(e.target.value)}
                rows={10} data-testid="transcript-input"
                className="w-full bg-white border border-[#B8D3DF] focus:border-[#00A2D4] focus:ring-2 focus:ring-[#00A2D4]/10 rounded-xl p-3.5 text-[13px] outline-none resize-none shadow-[0_4px_12px_rgba(7,83,154,0.06)]"
                placeholder="Paste meeting transcript…"
              />
              <button
                onClick={analyze} disabled={analyzing || !transcript.trim()} data-testid="run-analyze"
                className="w-full h-11 rounded-xl bg-[#07539A] hover:bg-[#063F73] disabled:opacity-50 text-[#FFFFFF] text-[13px] font-medium shadow-[0_8px_18px_rgba(7,83,154,0.22)] flex items-center justify-center gap-2"
              >
                {analyzing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                {analyzing ? "Analyzing…" : "Run AI Analysis"}
              </button>
            </div>
          </div>
        </div>
      )}

      {logOpen && <LogMeetingModal onClose={() => setLogOpen(false)} onLogged={async () => { setLogOpen(false); await load(); }} />}
    </div>
  );
}

/** Log a meeting from the Meetings screen.
 *
 * Elsewhere (Universe, Leads, Opportunity…) MeetingLogInline already knows which
 * entity it hangs off. Here there is no ambient entity, so the company has to be
 * chosen explicitly — /meetings/quick-log requires an entity_type + entity_id.
 */
function LogMeetingModal({ onClose, onLogged }) {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    title: "", company_id: "", meeting_type: "teams", participants: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/customer-options")
      .then(({ data }) => setCompanies(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Could not load companies"));
  }, []);

  async function save() {
    if (!form.company_id) return toast.error("Pick a company");
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.notes.trim()) return toast.error("Notes are required");
    setSaving(true);
    try {
      await api.post("/meetings/quick-log", {
        title: form.title.trim(),
        entity_type: "company",
        entity_id: form.company_id,
        meeting_type: form.meeting_type,
        participants: form.participants
          ? form.participants.split(",").map(s => s.trim()).filter(Boolean)
          : [],
        notes: form.notes.trim(),
      });
      toast.success("Meeting logged");
      await onLogged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#EFF8FC] border border-[#BFD9E5] rounded-[26px] shadow-[0_28px_70px_rgba(7,83,154,0.22)] w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()} data-testid="log-meeting-modal">
        <div className="p-5 border-b border-[#D5E7EF] flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl bg-white text-[#07539A] flex items-center justify-center"><CalendarClock size={15} /></span>
          <div className="font-display text-lg text-[#07539A] flex-1">Log a meeting</div>
          <button onClick={onClose} data-testid="log-meeting-close" className="w-8 h-8 rounded-full hover:bg-white text-[#607D8B] flex items-center justify-center"><X size={15} /></button>
        </div>
        <div className="p-5 md:p-6 space-y-4">
          <div>
            <label className="text-[11px] font-semibold text-[#07539A]">Company *</label>
            <select
              value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })}
              data-testid="lm-company"
              className="w-full mt-1.5 h-11 px-3.5 rounded-xl bg-white border border-[#B8D3DF] focus:border-[#00A2D4] focus:ring-2 focus:ring-[#00A2D4]/10 outline-none text-[13px] shadow-[0_4px_12px_rgba(7,83,154,0.06)]"
            >
              <option value="">Select a company…</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#07539A]">Title *</label>
            <input
              value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              data-testid="lm-title" placeholder="Pilot scoping call"
              className="w-full mt-1.5 h-11 px-3.5 rounded-xl bg-white border border-[#B8D3DF] focus:border-[#00A2D4] focus:ring-2 focus:ring-[#00A2D4]/10 outline-none text-[13px] shadow-[0_4px_12px_rgba(7,83,154,0.06)]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-[#07539A]">Type</label>
              <select
                value={form.meeting_type} onChange={e => setForm({ ...form, meeting_type: e.target.value })}
                data-testid="lm-type"
                className="w-full mt-1.5 h-11 px-3.5 rounded-xl bg-white border border-[#B8D3DF] focus:border-[#00A2D4] focus:ring-2 focus:ring-[#00A2D4]/10 outline-none text-[13px] shadow-[0_4px_12px_rgba(7,83,154,0.06)]"
              >
                {MEETING_TYPE_OPTIONS.filter(type => type !== "ask_niq").map(type => (
                  <option key={type} value={type}>{formatMeetingType(type)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#07539A]">Participants</label>
              <input
                value={form.participants} onChange={e => setForm({ ...form, participants: e.target.value })}
                data-testid="lm-participants" placeholder="Comma separated"
                className="w-full mt-1.5 h-11 px-3.5 rounded-xl bg-white border border-[#B8D3DF] focus:border-[#00A2D4] focus:ring-2 focus:ring-[#00A2D4]/10 outline-none text-[13px] shadow-[0_4px_12px_rgba(7,83,154,0.06)]"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#07539A]">Notes *</label>
            <textarea
              value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={6} data-testid="lm-notes" placeholder="What was discussed, decisions, next steps…"
              className="w-full mt-1.5 rounded-xl bg-white border border-[#B8D3DF] focus:border-[#00A2D4] focus:ring-2 focus:ring-[#00A2D4]/10 outline-none p-3.5 text-[13px] resize-none shadow-[0_4px_12px_rgba(7,83,154,0.06)]"
            />
            <p className="text-[10px] text-[#78909C] mt-1.5">
              Notes are analyzed on save — summary, action items and tasks are generated automatically.
            </p>
          </div>
          <button
            onClick={save} disabled={saving} data-testid="lm-save"
            className="w-full h-11 rounded-xl bg-[#07539A] hover:bg-[#063F73] disabled:opacity-50 text-[#FFFFFF] text-[13px] font-medium shadow-[0_8px_18px_rgba(7,83,154,0.22)] flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <CalendarClock size={13} />}
            {saving ? "Saving…" : "Log meeting"}
          </button>
        </div>
      </div>
    </div>
  );
}
